import React, { useMemo, useState } from 'react';
import { useT } from '../../i18n';
import { DEFAULT_TIMEFRAME, windowSeries } from './timeframes';

// Portfolio value over time, drawn as an inline SVG.
//
// No chart library: the whole shape is one polyline over a few hundred points,
// and the smallest charting dependency is larger than this file by two orders
// of magnitude. It scales with the viewport through a viewBox rather than a
// resize observer.
//
// The window is the PAGE's, not the chart's. It used to carry its own
// 1M/3M/1Y/All buttons, which sat directly under a period selector governing
// every other figure on the page and disagreed with it by default.

const VIEW_W = 1000;
const VIEW_H = 260;
const PAD_Y = 16;

const formatMoney = (n) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export const formatDate = (iso) => {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y.slice(2)}`;
};

export default function NavChart({
    navHistory,
    currency,
    netDeposits,
    scopeNote,
    timeframe = DEFAULT_TIMEFRAME,
    windowChange = null,
}) {
    const { t } = useT();
    const [hover, setHover] = useState(null);

    const points = useMemo(
        () => windowSeries(navHistory, timeframe)
            .map((p) => ({ date: p.date, total: Number(p.total) }))
            .filter((p) => Number.isFinite(p.total)),
        [navHistory, timeframe],
    );

    const geometry = useMemo(() => {
        if (points.length < 2) return null;

        const values = points.map((p) => p.total);
        const min = Math.min(...values);
        const max = Math.max(...values);
        // A flat series would divide by zero; give it a band so it draws as a
        // line through the middle rather than vanishing.
        const span = max - min || Math.abs(max) || 1;

        const x = (i) => (i / (points.length - 1)) * VIEW_W;
        const y = (v) => PAD_Y + (1 - (v - min) / span) * (VIEW_H - PAD_Y * 2);

        return {
            min,
            max,
            x,
            y,
            line: points.map((p, i) => `${x(i).toFixed(2)},${y(p.total).toFixed(2)}`).join(' '),
            area:
                `0,${VIEW_H} ` +
                points.map((p, i) => `${x(i).toFixed(2)},${y(p.total).toFixed(2)}`).join(' ') +
                ` ${VIEW_W},${VIEW_H}`,
        };
    }, [points]);

    if (!navHistory || navHistory.length < 2) return null;

    const first = points[0];
    const last = points[points.length - 1];
    // The page's own change when it has one: the line's endpoints would count
    // a wallet or the private book joining the total as a gain made in the
    // window — $1.8M of one, the day they joined. See `viewChange`.
    const change = windowChange
        ? windowChange.change
        : last && first ? last.total - first.total : 0;
    const active = hover !== null ? points[hover] : null;

    const onMove = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        if (!rect.width) return;
        const ratio = (event.clientX - rect.left) / rect.width;
        const index = Math.round(ratio * (points.length - 1));
        setHover(Math.max(0, Math.min(points.length - 1, index)));
    };

    // A deposit is a vertical step in this line, and reading that step as
    // performance is the mistake this whole page is arranged to prevent. So the
    // delta is called a change in value, and says outright when contributions
    // are part of it. Net P/L lives in the block above, where it belongs.
    const funded = Number(netDeposits);
    const includesDeposits = Number.isFinite(funded) && funded !== 0;

    return (
        <div className="navchart">
            <div className="navchart__head">
                <div className="navchart__readout">
                    <span className="navchart__title">{t('navChart.title')}</span>
                    {active ? (
                        <>
                            <span className="navchart__value">{formatMoney(active.total)}</span>
                            <span className="navchart__date">{formatDate(active.date)}</span>
                        </>
                    ) : (
                        <>
                            <span className="navchart__value">
                                {last ? formatMoney(last.total) : '—'}
                            </span>
                            <span className="navchart__change">
                                {change < 0 ? '−' : '+'}
                                {t(includesDeposits ? 'navChart.changeWithDeposits' : 'navChart.change', {
                                    amount: formatMoney(Math.abs(change)),
                                    currency,
                                })}
                            </span>
                        </>
                    )}
                </div>
            </div>

            {geometry && (
                <svg
                    className="navchart__svg"
                    viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                    preserveAspectRatio="none"
                    role="img"
                    aria-label={`${t('navChart.title')} · ${t('performance.period', {
                        from: first.date, to: last.date,
                    })}`}
                    onMouseMove={onMove}
                    onMouseLeave={() => setHover(null)}
                >
                    <defs>
                        <linearGradient id="navfill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#8ffca0" stopOpacity="0.22" />
                            <stop offset="100%" stopColor="#8ffca0" stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    <polygon points={geometry.area} fill="url(#navfill)" />
                    <polyline
                        points={geometry.line}
                        fill="none"
                        stroke="#8ffca0"
                        strokeWidth="2"
                        vectorEffect="non-scaling-stroke"
                    />
                    {active && (
                        <>
                            <line
                                x1={geometry.x(hover)}
                                y1="0"
                                x2={geometry.x(hover)}
                                y2={VIEW_H}
                                stroke="#8ffca0"
                                strokeOpacity="0.35"
                                strokeWidth="1"
                                vectorEffect="non-scaling-stroke"
                            />
                            <circle
                                cx={geometry.x(hover)}
                                cy={geometry.y(active.total)}
                                r="4"
                                fill="#8ffca0"
                            />
                        </>
                    )}
                </svg>
            )}

            <div className="navchart__axis">
                <span>{first ? formatDate(first.date) : ''}</span>
                <span>{last ? formatDate(last.date) : ''}</span>
            </div>

            {/* Sits with the chart rather than in the page's warnings list: it
                qualifies this figure, it is not a problem to be fixed. */}
            {scopeNote && <p className="navchart__scope">{scopeNote}</p>}
        </div>
    );
}
